import { useNavigate } from 'react-router-dom';
import { useTheme } from 'styled-components';
import S from './AlertTypesStyles';
import { ThemeType } from '../../../theme/themeType';
import useGetFleetId from '../../../API/hooks/useGetFleetId';
import useGetAlertTypes from '../../../API/hooks/alerts/useGetAlertsTypes';
import DoghnutChartBigWidget from '../../common/DoghnutChartBigWidget/DoghnutChartBigWidget';
import DashboardWidgetMenu from '../DashboardWidgetMenu/DashboardWidgetMenu';
import {
  AlertTypesDognutConfig,
  AlertTypesKeys,
} from '../../../API/types/Alerts';
import { getEndOfTheDay } from '../../../utils/dateUtils';
import { getFilterFields, queryFilterByGroups } from '../../../utils/queryFilters';

const buildAlertTypesChartConfig = (theme: ThemeType): AlertTypesDognutConfig => ({
  cards: {
    IMU: {
      color: theme.charts.alertImu,
      label: 'IMU',
    },
    frontCamera: {
      color: theme.charts.alertFrontCamera,
      label: 'Front camera',
    },
    inwardCamera: {
      color: theme.charts.alertInwardCamera,
      label: 'Inward camera',
    },
    cloud: {
      color: theme.charts.alertCloud,
      label: 'Cloud',
    },
  },
  title: 'Sensor Types',
});

const PERIOD_LABEL: Record<string, string> = {
  daily: 'Today',
  weekly: 'This week',
  monthly: 'This month',
  all: 'All time',
};

type Props = {
  timeRange: Record<string, string[]>;
  groupFilters?: Record<string, any>;
  onMove?: () => void;
  onRemove?: () => void;
};

function AlertTypes({
  timeRange,
  groupFilters = {},
  onMove,
  onRemove,
}: Props): JSX.Element {
  const fleetId = useGetFleetId();
  const theme = useTheme() as ThemeType;
  const alertTypesChartConfig = buildAlertTypesChartConfig(theme);
  let period = 'monthly';
  if (timeRange?.period?.[0]) {
    if (timeRange.period[0] !== 'custom') {
      period = timeRange.period?.[0];
    } else {
      period = `&from=${timeRange.startDate?.[0]}&to=${timeRange.endDate?.[0]}`;
    }
  }

  const filters = {
    startDate: [timeRange.startDate?.[0]],
    endDate: [(timeRange.endDate?.[0] || getEndOfTheDay().toISOString())],
    period,
  };

  const isAllTime = timeRange.period?.[0] === 'custom'
    && timeRange.startDate?.[0] === new Date(0).toISOString();
  const periodLabel = isAllTime ? PERIOD_LABEL.all : PERIOD_LABEL[period];

  const { data: typesChartData, isLoading } = useGetAlertTypes(
    fleetId,
    `?limit=1000&skip=0${getFilterFields(filters, ['period'])}${queryFilterByGroups(groupFilters)}`,
  );

  const navigate = useNavigate();
  const handleClick = (): void => {
    navigate('/alerts');
  };

  Object.keys(alertTypesChartConfig.cards).forEach((key: string): void => {
    alertTypesChartConfig.cards[key as AlertTypesKeys].count = typesChartData?.[key as AlertTypesKeys] || 0;
  });

  return (
    <S.Wrapper>
      <div className="header">
        <h3 className="header-title">
          Sensor Types
          {periodLabel && ` • ${periodLabel}`}
        </h3>
        <DashboardWidgetMenu
          onViewAll={handleClick}
          onMove={onMove}
          onRemove={onRemove}
        />
      </div>
      <div className="alert-types-container">
        <DoghnutChartBigWidget
          percentage
          data={alertTypesChartConfig}
          loading={isLoading}
          fourRows={true as boolean}
          emptyText="No alerts to show"
        />
      </div>
    </S.Wrapper>
  );
}

export default AlertTypes;
